/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import HeaderDropdownToggle from '../content/CustomDropdowns/HeaderDropdownToggle'
import { categoryAccepted } from '../../util/consent.util'
import { BS_THEME_CHANGE_EVENT, getStoredColorScheme, setStoredColorScheme } from '../../util/colorScheme'
import { getLandingPagePath } from '../../../_augmentt/utils/utils'

const userFlowToken = process.env.REACT_APP_USER_FLOW_TOKEN || 'w46hpmyl65hapbvqa3cup7qt5y'
const userFlowConsentCategory = 'functional'
const consentRetryLimit = 20
const consentRetryDelay = 500

function UserProfile(props) {
  const { showAvatar } = props

  const intl = useIntl()
  const currentUser = useSelector(({ user }) => user.data)
  const permissions = useSelector(({ userPermissions }) => userPermissions.data?.permissions)
  const [canLoadUserflow, setCanLoadUserflow] = useState(false)
  const [colorScheme, setColorScheme] = useState(getStoredColorScheme)

  const trackUser = useCallback(async () => {
    try {
      const { loadUserflow } = await import('userflow.js')
      const userflow = await loadUserflow()

      userflow.init(userFlowToken)
      userflow.identify(currentUser.id, {
        name: `${currentUser.first_name} ${currentUser.last_name}`,
        email: currentUser.email,
        landing_page: currentUser.landing_page ?? getLandingPagePath(permissions),
        landing_page_is_default: currentUser.landing_page == null,
      })
    } catch (error) {
      // Userflow failed to load – non-critical, ignored
    }
  }, [currentUser, permissions])

  useEffect(() => {
    let consentRetryCount = 0
    let consentResolved = false
    let consentRetryTimerId = null

    function syncUserflowConsent(rawConsent) {
      consentResolved = true

      if (consentRetryTimerId) {
        window.clearTimeout(consentRetryTimerId)
        consentRetryTimerId = null
      }

      setCanLoadUserflow(categoryAccepted(rawConsent, userFlowConsentCategory))
    }

    function syncUserflowConsentFromApi() {
      if (consentResolved) {
        return
      }

      if (typeof window.getCkyConsent === 'function') {
        syncUserflowConsent(window.getCkyConsent())
        return
      }

      if (consentRetryCount >= consentRetryLimit) {
        return
      }

      consentRetryCount += 1
      consentRetryTimerId = window.setTimeout(syncUserflowConsentFromApi, consentRetryDelay)
    }

    function handleBannerLoad(eventData) {
      if (eventData && eventData.detail) {
        syncUserflowConsent(eventData.detail)
        return
      }
      syncUserflowConsentFromApi()
    }

    document.addEventListener('cookieyes_banner_load', handleBannerLoad)
    syncUserflowConsentFromApi()

    return () => {
      if (consentRetryTimerId) {
        window.clearTimeout(consentRetryTimerId)
      }

      document.removeEventListener('cookieyes_banner_load', handleBannerLoad)
    }
  }, [])

  useEffect(() => {
    if (!currentUser || !canLoadUserflow) {
      return
    }

    trackUser()
  }, [canLoadUserflow, currentUser, trackUser])

  // Keep the dark-mode switch aligned with `data-bs-theme` (same tab, other code paths, or storage events).
  useEffect(() => {
    const syncFromDom = () => {
      setColorScheme(getStoredColorScheme())
    }
    window.addEventListener(BS_THEME_CHANGE_EVENT, syncFromDom)
    window.addEventListener('storage', syncFromDom)
    return () => {
      window.removeEventListener(BS_THEME_CHANGE_EVENT, syncFromDom)
      window.removeEventListener('storage', syncFromDom)
    }
  }, [])

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {currentUser && (
        <Dropdown className="header__topbar-item header__topbar-item--user" drop="down" align="end">
          <Dropdown.Toggle as={HeaderDropdownToggle} id="dropdown-toggle-user-profile">
            <div className="header__topbar-user">
              {/* eslint-disable-next-line no-nested-ternary */}
              {showAvatar && currentUser.avatar ? (
                <img alt="Avatar" src={currentUser.avatar} />
              ) : showAvatar && !currentUser.avatar ? (
                <img alt="Avatar" src="/media/users/default.jpg" />
              ) : null}
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-fit dropdown-menu-end dropdown-menu-anim dropdown-menu-top-unround dropdown-menu-lg">
            <div className="user-card user-card--skin-dark notification-item-padding-x">
              <div className="user-card__name">
                {currentUser.first_name} {currentUser.last_name}
              </div>
              <div className="user-card__badge" />
            </div>
            <div className="notification">
              <Dropdown.Item as={Link} to="/user-profile" className="notification__item">
                <div className="notification__item-icon">
                  <i className="fa fa-user font-brand" />
                </div>
                <div className="notification__item-details">
                  <div className="notification__item-title font-bold">
                    <FormattedMessage id="USERPROFILE.MENU_YOUR_PROFILE" />
                  </div>
                  <div className="notification__item-time">
                    <FormattedMessage id="USERPROFILE.MENU_PROFILE_DETAILS" />
                  </div>
                </div>
              </Dropdown.Item>
              <button type="button" className="notification__item cky-banner-element">
                <div className="notification__item-icon">
                  <i className="fa fa-cookie font-brand" />
                </div>
                <div className="notification__item-details">
                  <div className="notification__item-title font-bold">
                    <FormattedMessage id="USERPROFILE.MENU_COOKIE_SETTINGS" />
                  </div>
                  <div className="notification__item-time">
                    <FormattedMessage id="USERPROFILE.MENU_COOKIE_SETTINGS_DETAILS" />
                  </div>
                </div>
              </button>
              <label
                className="notification__item notification__item--toggle mb-0 w-100"
                htmlFor="user-profile-dark-mode"
              >
                <div className="notification__item-icon">
                  <i className="fa fa-moon font-brand" aria-hidden />
                </div>
                <div className="notification__item-details flex-grow-1">
                  <div className="notification__item-title font-bold">
                    <FormattedMessage id="USERPROFILE.MENU_DARK_MODE" />
                  </div>
                  <div className="notification__item-time">
                    <FormattedMessage id="USERPROFILE.MENU_DARK_MODE_DETAILS" />
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id="user-profile-dark-mode"
                  className="mb-0 ms-2"
                  checked={colorScheme === 'dark'}
                  aria-label={intl.formatMessage({ id: 'USERPROFILE.MENU_DARK_MODE' })}
                  onChange={(e) => {
                    const next = e.target.checked ? 'dark' : 'light'
                    setColorScheme(next)
                    setStoredColorScheme(next)
                  }}
                />
              </label>
              <div className="notification__custom">
                <Link to="/logout" className="btn btn-label-brand btn-sm btn-bold">
                  <FormattedMessage id="GENERAL.SIGN_OUT" />
                </Link>
              </div>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      )}
    </>
  )
}

export default UserProfile
